import { type ReactNode, type RefObject, useId, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { type Layer, LayerStack } from "./modal-stack";
import { holdScroll } from "./scroll-lock";

const layers = new LayerStack();
/** The page as it was before the first layer opened, put back when the last one closes. */
let held: { restore(): void } | null = null;

function onKey(e: KeyboardEvent): void {
  const top = layers.top;
  if (e.key !== "Escape" || top === null) return;
  e.stopPropagation();
  top.close();
}

function holdPage(): { restore(): void } {
  const app = document.getElementById("root");
  const madeInert = app !== null && !app.hasAttribute("inert");
  if (madeInert) app.setAttribute("inert", "");
  const release = holdScroll();
  window.addEventListener("keydown", onKey, true);
  return {
    restore() {
      window.removeEventListener("keydown", onKey, true);
      if (madeInert) app.removeAttribute("inert");
      release();
    },
  };
}

const CONTROLS =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The first control in `node`, `node` itself included. */
function firstControl(node: Node): HTMLElement | null {
  if (!(node instanceof HTMLElement)) return null;
  return node.matches(CONTROLS) ? node : node.querySelector<HTMLElement>(CONTROLS);
}

/**
 * The control standing where `gone` was removed: the first control of the nearest sibling after the removed node,
 * else of the nearest one before it, so Tab goes on from the same place.
 */
function standIn(gone: Node, records: readonly MutationRecord[], layer: HTMLElement): HTMLElement | null {
  const removal = (node: Node) =>
    records.find((r) => Array.from(r.removedNodes).some((n) => n === node || n.contains(node)));
  const record = removal(gone);
  if (!record) return null;
  for (const step of ["nextSibling", "previousSibling"] as const) {
    // A sibling removed with it is gone too: go on from where its own removal left the list.
    let start = record[step];
    for (let hops = 0; start !== null && !layer.contains(start) && hops < records.length; hops++)
      start = removal(start)?.[step] ?? null;
    for (let n = start; n !== null; n = n[step]) {
      const control = firstControl(n);
      if (control !== null && layer.contains(control)) return control;
    }
  }
  return null;
}

/** The child of `<body>` a portaled dialog lives in. */
function layerNode(el: HTMLElement | null): HTMLElement | null {
  let node = el;
  while (node !== null && node.parentElement !== document.body) node = node.parentElement;
  return node;
}

/**
 * A dialog over the page, portaled to `<body>`: the app behind it inert and
 * still, so Tab stays in the dialog, with the focus on `initialFocus` and
 * handed back to whatever opened it on close. Dialogs stack: one opened over
 * another makes the one under it inert too, until it closes. Escape is caught
 * on the way down and kept, so one press closes only the top layer, not a
 * dialog or a photo's lightbox under it.
 */
export function useModalLayer(initialFocus: RefObject<HTMLElement | null>, onClose: () => void): void {
  const close = useRef(onClose);
  useLayoutEffect(() => {
    close.current = onClose;
  });

  useLayoutEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const node = layerNode(initialFocus.current);
    const layer: Layer = { node, close: () => close.current() };
    held ??= holdPage();
    layers.open(layer);
    // The control last focused in the dialog, to know where one removed stood.
    let last: HTMLElement | null = null;
    const remember = (e: FocusEvent) => {
      if (e.target instanceof HTMLElement && node?.contains(e.target)) last = e.target;
    };
    window.addEventListener("focusin", remember, true);
    initialFocus.current?.focus();
    // A control that leaves the dialog (a line removed, a button a stage takes away) drops the focus to the page;
    // it goes back into the dialog instead, to the control standing in its place.
    const keep =
      node !== null && typeof MutationObserver !== "undefined"
        ? new MutationObserver((records) => {
            const active = document.activeElement;
            if (layers.top !== layer || (active !== null && active !== document.body)) return;
            const near = last !== null && !node.contains(last) ? standIn(last, records, node) : null;
            if (near !== null) {
              near.focus();
              return;
            }
            const dialog = node.querySelector<HTMLElement>('[role="dialog"][tabindex], [role="alertdialog"][tabindex]');
            (dialog ?? initialFocus.current)?.focus({ preventScroll: true });
          })
        : null;
    if (node !== null) keep?.observe(node, { childList: true, subtree: true });
    return () => {
      window.removeEventListener("focusin", remember, true);
      keep?.disconnect();
      const wasTop = layers.close(layer);
      if (layers.size === 0) {
        held?.restore();
        held = null;
      }
      if (!wasTop || opener === null) return;
      opener.focus();
      // An opener the same commit takes out of its disabled state takes the focus once that commit is over.
      if (document.activeElement !== opener)
        queueMicrotask(() => {
          const active = document.activeElement;
          if (!node?.isConnected && (active === null || active === document.body)) opener.focus();
        });
    };
  }, [initialFocus]);
}

/** A question with two answers, the safe one focused. Closing it is the safe answer, except while it is acting. */
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  busyLabel,
  cancelLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: ReactNode;
  confirmLabel: string;
  busyLabel: string;
  cancelLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const keep = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const messageId = useId();
  const close = () => {
    if (!busy) onCancel();
  };
  useModalLayer(keep, close);

  return createPortal(
    <div className="scrim items-end justify-center sm:items-center sm:p-4">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={close}
        className="absolute inset-0 cursor-default"
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        className="modal relative w-full max-w-sm p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:pb-5"
      >
        <h2 id={titleId} className="text-base font-semibold text-white">
          {title}
        </h2>
        <p id={messageId} className="mt-2 text-sm text-muted">
          {message}
        </p>
        {/* Held, not disabled, while acting: the focus stays on the answer pressed. */}
        <div className="mt-5 flex gap-3">
          <button
            ref={keep}
            type="button"
            onClick={close}
            aria-disabled={busy || undefined}
            className="btn-secondary flex-1 aria-disabled:cursor-default aria-disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              if (!busy) onConfirm();
            }}
            aria-disabled={busy || undefined}
            className="btn-destructive flex-1 aria-disabled:cursor-default aria-disabled:opacity-50"
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
